import { useEffect, useCallback } from 'react';
import { getSocket } from './client';
import { CLIENT_EVENTS, SERVER_EVENTS } from './events';
import { gameStore } from '@/store/gameStore';
import { sessionStore } from '@/store/sessionStore';
import { toast } from '@/store/uiStore';
import type {
  JoinedPayload,
  QuestionStartedPayload,
  QuestionRevealedPayload,
  RoundEndedPayload,
  AnswerCountPayload,
  ParticipantJoinedPayload,
  ParticipantLeftPayload,
  WordChainData,
  WordChainRevealData,
} from '@/store/gameStore';

type Role = 'player' | 'host';

export function useGameSocket(roundId: string, role: Role, pinCode?: string) {
  useEffect(() => {
    const token = sessionStore.getState().token;
    if (!token) return;

    if (gameStore.getState().roundId !== roundId) {
      gameStore.getState().reset();
    }

    const socket = getSocket(token);
    const g = gameStore.getState();

    const onConnect = () => {
      g.setConnectionStatus('connected');
      if (role === 'host') {
        socket.emit(CLIENT_EVENTS.HOST_JOIN, { round_id: roundId });
      } else {
        const alreadyJoined = gameStore.getState().roundId === roundId;
        if (alreadyJoined) {
          socket.emit(CLIENT_EVENTS.REJOIN_ROOM, { round_id: roundId });
        } else {
          socket.emit(CLIENT_EVENTS.JOIN_ROOM, {
            round_id: roundId,
            ...(pinCode ? { pin_code: pinCode } : {}),
          });
        }
      }
    };

    const onDisconnect = () => {
      g.setConnectionStatus('disconnected');
    };

    const onReconnectAttempt = () => {
      g.setConnectionStatus('reconnecting');
    };

    const onReconnect = () => {
      g.setConnectionStatus('connected');
      const currentRound = gameStore.getState().roundId;
      if (currentRound) {
        const event = role === 'host' ? CLIENT_EVENTS.HOST_JOIN : CLIENT_EVENTS.REJOIN_ROOM;
        socket.emit(event, { round_id: currentRound });
      }
    };

    const onRoomJoined = (raw: any) => {
      let phase: JoinedPayload['phase'];
      if (raw.status === 'OPEN') phase = 'waiting';
      else if (raw.status === 'IN_PROGRESS') {
        if (raw.game_type === 'word_chain') {
          phase = 'word_chain';
        } else {
          phase = raw.current_question ? 'question' : 'waiting';
        }
      }
      else if (raw.status === 'ENDED') phase = 'ended';
      else phase = 'waiting';

      const participants = (raw.participants ?? []).map((p: any, i: number) => ({
        rank: i + 1,
        participant_id: p.participant_id,
        display_name: p.display_name,
        avatar_url: p.avatar_url,
        total_score: p.total_score ?? 0,
        correct_count: 0,
      }));

      const adapted: JoinedPayload = {
        round_id: raw.round_id,
        phase,
        participants,
        is_spectator: raw.is_spectator ?? false,
        my_participant_id: raw.my_participant_id ?? undefined,
        current_question: raw.current_question ?? null,
      };
      g.setRoundJoined(adapted);
    };

    const onQuestionStarted = (d: QuestionStartedPayload) => {
      // setQuestionStarted already resets startingAt to null internally.
      // Calling setStartingAt(null) separately would trigger an extra render
      // with phase='waiting', causing root to unmount/remount and losing the
      // wordScrambleInstruction paint on the first question.
      g.setQuestionStarted(d);
    };

    const onAnswerReceived = () => g.setAnswerSubmitted();

    const onQuestionRevealed = (raw: any) => {
      const leaderboard = raw.leaderboard_top ?? raw.leaderboard_top_n ?? [];
      const adapted: QuestionRevealedPayload = {
        question_id: raw.question_id,
        canonical_answer: raw.canonical_answer,
        explanation: raw.explanation ?? null,
        reveal_ends_at: raw.reveal_ends_at ?? (Date.now() + 30_000),
        per_player_result: (raw.per_player_result ?? []).map((r: any) => ({
          participant_id: r.participant_id,
          display_name: r.display_name ?? null,
          avatar_url: r.avatar_url ?? null,
          is_correct: r.is_correct,
          score_awarded: r.score_awarded ?? 0,
          response_time_ms: r.response_time_ms ?? null,
          answer_text: r.answer_text ?? null,
          new_total_score: r.new_total_score ?? 0,
          rank_after: r.rank_after ?? 999,
        })),
        leaderboard_top_n: leaderboard.map((p: any, i: number) => ({
          rank: p.rank ?? i + 1,
          participant_id: p.participant_id,
          display_name: p.display_name,
          avatar_url: p.avatar_url,
          total_score: p.total_score ?? 0,
          correct_count: p.correct_count ?? 0,
        })),
      };
      const myId = gameStore.getState().myParticipantId;
      g.setQuestionRevealed(adapted, myId);
    };

    const onScoreboardUpdate = (d: { entries: ReturnType<typeof gameStore.getState>['participants'] }) => {
      g.setScoreboard(d.entries);
    };

    const onAnswerCountUpdated = (d: AnswerCountPayload) => {
      g.setAnswerCount(d);
    };

    const onRoundEnded = (raw: any) => {
      const adapted: RoundEndedPayload = {
        round_id: raw.round_id,
        ended_at: typeof raw.ended_at === 'number'
          ? new Date(raw.ended_at).toISOString()
          : (raw.ended_at ?? ''),
        final_leaderboard: (raw.leaderboard ?? raw.final_leaderboard ?? []).map((p: any, i: number) => ({
          rank: p.rank ?? i + 1,
          participant_id: p.participant_id,
          display_name: p.display_name,
          avatar_url: p.avatar_url,
          total_score: p.total_score ?? 0,
          correct_count: p.correct_count ?? 0,
        })),
      };
      g.setRoundEnded(adapted);
    };

    const onParticipantJoined = (d: ParticipantJoinedPayload) => {
      gameStore.getState().addParticipant(d);
      gameStore.getState().addNotification({
        type: 'join',
        message: `${d.display_name ?? 'Người chơi'} đã tham gia`,
      });
    };

    const onParticipantLeft = (d: ParticipantLeftPayload) => {
      const state = gameStore.getState();
      const leaving = state.participants.find((p) => p.participant_id === d.participant_id);
      state.removeParticipant(d);
      state.addNotification({
        type: 'leave',
        message: `${leaving?.display_name ?? 'Người chơi'} đã rời phòng`,
      });
    };

    const onQuestionStarting = (raw: any) => {
      gameStore.getState().setQuestionStarting({
        order_index: raw.order_index,
        total_questions: raw.total_questions,
        starts_at: raw.starts_at,
      });
    };

    const onRoundStarting = (raw: any) => {
      gameStore.getState().setStartingAt(raw.starts_at);
      gameStore.getState().addNotification({
        type: 'countdown',
        message: `Game sẽ bắt đầu sau ${raw.countdown_sec}s`,
      });
    };

    const onWordChainStarted = (raw: any) => {
      const data: WordChainData = {
        game_question_id: raw.game_question_id,
        chain_hints: raw.chain_hints ?? [],
        total_words: raw.total_words ?? (raw.chain_hints?.length ?? 0),
        time_limit_sec: raw.time_limit_sec,
        server_started_at: raw.server_started_at ?? Date.now(),
        question_text: raw.question_text,
      };
      gameStore.getState().setWordChainStarted(data);
    };

    const onWordChainSubmitCountUpdated = (raw: any) => {
      gameStore.getState().setWordChainSubmitCount({
        submitted_count: raw.submitted_count,
        total_players: raw.total_players,
      });
    };

    const onWordChainRevealed = (raw: any) => {
      const data: WordChainRevealData = {
        canonical_answer: raw.canonical_answer ?? '',
        correct_words: raw.correct_words ?? [],
        explanation: raw.explanation ?? null,
        per_player_result: (raw.per_player_result ?? []).map((r: any) => ({
          participant_id: r.participant_id,
          display_name: r.display_name ?? null,
          avatar_url: r.avatar_url ?? null,
          submitted_words: r.submitted_words ?? [],
          correct_mask: r.correct_mask ?? [],
          score_awarded: r.score_awarded ?? 0,
          response_time_ms: r.response_time_ms ?? null,
          rank: r.rank ?? 999,
        })),
      };
      gameStore.getState().setWordChainRevealed(data);
    };

    const onRoomClosed = () => {
      // Component handles navigate
    };

    const onError = (err: { code: string; message: string; fatal?: boolean }) => {
      console.warn('[socket error]', err);
      if (err.code !== 'REPLACED') {
        toast.error(`Socket: ${err.code}`, err.message);
      }
      if (err.fatal) {
        gameStore.getState().reset();
      }
    };

    const onAuthRevoked = () => {
      sessionStore.getState().logout();
    };

    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);
    socket.io.on('reconnect_attempt', onReconnectAttempt);
    socket.io.on('reconnect', onReconnect);

    socket.on(SERVER_EVENTS.ROOM_JOINED, onRoomJoined);
    socket.on(SERVER_EVENTS.QUESTION_STARTED, onQuestionStarted);
    socket.on(SERVER_EVENTS.ANSWER_RECEIVED, onAnswerReceived);
    socket.on(SERVER_EVENTS.QUESTION_REVEALED, onQuestionRevealed);
    socket.on(SERVER_EVENTS.ANSWER_COUNT_UPDATED, onAnswerCountUpdated);
    socket.on(SERVER_EVENTS.ROUND_ENDED, onRoundEnded);
    socket.on(SERVER_EVENTS.PARTICIPANT_JOINED, onParticipantJoined);
    socket.on(SERVER_EVENTS.PARTICIPANT_LEFT, onParticipantLeft);
    socket.on(SERVER_EVENTS.QUESTION_STARTING, onQuestionStarting);
    socket.on(SERVER_EVENTS.ROUND_STARTING, onRoundStarting);
    const onWordChainWordResult = (data: { word_index: number; correct: boolean; score_delta: number }) => {
      gameStore.getState().addWordChainWordResult(data);
    };

    const onWordChainWordCorrect = (data: { word_index: number; participant_id: string; display_name: string | null; avatar_url: string | null }) => {
      gameStore.getState().addWordChainWordCorrect(data);
    };

    const onWordChainWordPhaseStarted = (data: { round_id: string; word_index: number; starts_at: number; time_limit_sec: number }) => {
      gameStore.getState().setWordChainWordPhaseStarted(data);
    };

    const onWordChainWordPhaseEnded = (data: { round_id: string; word_index: number; leaderboard: Array<{ rank: number; participant_id: string; display_name: string | null; avatar_url: string | null; total_score: number }> }) => {
      gameStore.getState().setWordChainWordPhaseEnded(data);
    };

    socket.on(SERVER_EVENTS.WORD_CHAIN_STARTED, onWordChainStarted);
    socket.on(SERVER_EVENTS.WORD_CHAIN_SUBMIT_COUNT_UPDATED, onWordChainSubmitCountUpdated);
    socket.on(SERVER_EVENTS.WORD_CHAIN_REVEALED, onWordChainRevealed);
    socket.on(SERVER_EVENTS.WORD_CHAIN_WORD_RESULT, onWordChainWordResult);
    socket.on(SERVER_EVENTS.WORD_CHAIN_WORD_CORRECT, onWordChainWordCorrect);
    socket.on(SERVER_EVENTS.WORD_CHAIN_WORD_PHASE_STARTED, onWordChainWordPhaseStarted);
    socket.on(SERVER_EVENTS.WORD_CHAIN_WORD_PHASE_ENDED, onWordChainWordPhaseEnded);
    socket.on(SERVER_EVENTS.ROOM_CLOSED, onRoomClosed);
    socket.on(SERVER_EVENTS.ERROR, onError);
    socket.on(SERVER_EVENTS.AUTH_REVOKED, onAuthRevoked);
    socket.on(SERVER_EVENTS.CHAT_MESSAGE, (p) => {
      gameStore.getState().addChatMessage(p);
    });

    if (socket.connected) {
      onConnect();
    } else {
      socket.connect();
    }

    return () => {
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);
      socket.io.off('reconnect_attempt', onReconnectAttempt);
      socket.io.off('reconnect', onReconnect);

      socket.off(SERVER_EVENTS.ROOM_JOINED, onRoomJoined);
      socket.off(SERVER_EVENTS.QUESTION_STARTED, onQuestionStarted);
      socket.off(SERVER_EVENTS.ANSWER_RECEIVED, onAnswerReceived);
      socket.off(SERVER_EVENTS.QUESTION_REVEALED, onQuestionRevealed);
      socket.off(SERVER_EVENTS.ANSWER_COUNT_UPDATED, onAnswerCountUpdated);
      socket.off(SERVER_EVENTS.ROUND_ENDED, onRoundEnded);
      socket.off(SERVER_EVENTS.PARTICIPANT_JOINED, onParticipantJoined);
      socket.off(SERVER_EVENTS.PARTICIPANT_LEFT, onParticipantLeft);
      socket.off(SERVER_EVENTS.QUESTION_STARTING, onQuestionStarting);
      socket.off(SERVER_EVENTS.ROUND_STARTING, onRoundStarting);
      socket.off(SERVER_EVENTS.WORD_CHAIN_STARTED, onWordChainStarted);
      socket.off(SERVER_EVENTS.WORD_CHAIN_SUBMIT_COUNT_UPDATED, onWordChainSubmitCountUpdated);
      socket.off(SERVER_EVENTS.WORD_CHAIN_REVEALED, onWordChainRevealed);
      socket.off(SERVER_EVENTS.WORD_CHAIN_WORD_RESULT, onWordChainWordResult);
      socket.off(SERVER_EVENTS.WORD_CHAIN_WORD_CORRECT, onWordChainWordCorrect);
      socket.off(SERVER_EVENTS.WORD_CHAIN_WORD_PHASE_STARTED, onWordChainWordPhaseStarted);
      socket.off(SERVER_EVENTS.WORD_CHAIN_WORD_PHASE_ENDED, onWordChainWordPhaseEnded);
      socket.off(SERVER_EVENTS.ROOM_CLOSED, onRoomClosed);
      socket.off(SERVER_EVENTS.ERROR, onError);
      socket.off(SERVER_EVENTS.AUTH_REVOKED, onAuthRevoked);
      socket.off(SERVER_EVENTS.CHAT_MESSAGE);
    };
  }, [roundId, role, pinCode]);
}

export function useSubmitAnswer(roundId: string) {
  return useCallback(
    (questionId: string, orderIndex: number, answerText: string) => {
      const token = sessionStore.getState().token;
      if (!token) return;
      const socket = getSocket(token);
      socket.emit(CLIENT_EVENTS.SUBMIT_ANSWER, {
        round_id: roundId,
        question_id: questionId,
        raw_answer: answerText,
        client_submitted_at: Date.now(),
      });
      gameStore.getState().setAnswerSubmitted();
    },
    [roundId],
  );
}

export function useSubmitWordChain(roundId: string) {
  return useCallback(
    (words: string[]) => {
      const token = sessionStore.getState().token;
      if (!token) return;
      const socket = getSocket(token);
      const gameQuestionId = gameStore.getState().wordChainData?.game_question_id;
      if (!gameQuestionId) return;
      socket.emit(CLIENT_EVENTS.WORD_CHAIN_SUBMIT, {
        round_id: roundId,
        game_question_id: gameQuestionId,
        answers: words,
        client_submitted_at: Date.now(),
      });
      gameStore.getState().setAnswerSubmitted();
    },
    [roundId],
  );
}

export function useSendChatMessage(roundId: string) {
  return useCallback(
    (message: string) => {
      const token = sessionStore.getState().token;
      if (!token || !message.trim()) return;
      const socket = getSocket(token);
      socket.emit(CLIENT_EVENTS.CHAT_MESSAGE, { round_id: roundId, message: message.trim() });
    },
    [roundId],
  );
}

export function useSubmitWordChainWord(roundId: string) {
  return useCallback(
    (wordIndex: number, answer: string) => {
      const token = sessionStore.getState().token;
      if (!token) return;
      const socket = getSocket(token);
      const gameQuestionId = gameStore.getState().wordChainData?.game_question_id;
      if (!gameQuestionId) return;
      socket.emit(CLIENT_EVENTS.WORD_CHAIN_SUBMIT_WORD, {
        round_id: roundId,
        game_question_id: gameQuestionId,
        word_index: wordIndex,
        answer,
      });
    },
    [roundId],
  );
}

export function useHostControls(roundId: string) {
  const emit = useCallback(
    (event: string, payload?: Record<string, unknown>) => {
      const token = sessionStore.getState().token;
      if (!token) return;
      const socket = getSocket(token);
      socket.emit(event, { round_id: roundId, ...payload });
    },
    [roundId],
  );

  return {
    startRound: () => emit(CLIENT_EVENTS.HOST_START_ROUND),
    revealAnswer: (questionId: string) =>
      emit(CLIENT_EVENTS.HOST_REVEAL_ANSWER, { question_id: questionId }),
    nextQuestion: (fromQuestionId: string) =>
      emit(CLIENT_EVENTS.HOST_NEXT_QUESTION, { from_question_id: fromQuestionId }),
    endRound: () => emit(CLIENT_EVENTS.HOST_END_ROUND),
  };
}
